import React, { useState, useEffect } from 'react';
import { func } from 'prop-types';
import { Box, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import Typography from '@avon/component-library/dist/Typography';
import Dropdown from '@avon/component-library/dist/Dropdown';
import Button from '@avon/component-library/dist/Button';
import { useError } from '@avon/component-library';
import cookies from 'react-cookies';
import loginBg from '../styles/assets/loginBg.svg';
import avonGrowSvg from '../styles/assets/avon_grow.svg';
import { getMarkets } from '../utils/api/endpoints';
import getDeviceType from '../utils/getDeviceType';

const useStyles = makeStyles(theme => ({
  pageContainer: {
    backgroundImage: `linear-gradient(131.14deg, rgba(127, 40, 196, 0.7) 4.53%, rgba(226, 25, 124, 0.7) 70.61%, rgba(229, 35, 27, 0.7) 102.64%), url(${loginBg})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    height: '100%',
    backgroundPosition: '1px 0',
    position: 'relative',
    [theme.breakpoints.between('sm', 'md')]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  cardContainer: {
    position: 'absolute',
    top: '50%',
    marginTop: '-272px',
    right: '100px',
    [theme.breakpoints.between('sm', 'md')]: {
      right: 'unset',
    },
  },
  boxContainer: {
    background: '#FFFFFF',
    width: 454,
    height: 543,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderRadius: 8,
    padding: '48px 0',
    margin: '0 auto',
  },
  placeHolder: {
    display: p => (p.deviceType === 'tablet' ? 'none' : 'block'),
  },
  boxContainerTablet: {
    background: '#FFFFFF',
    width: 453,
    height: 543,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    margin: 'auto',
    borderRadius: 8,
    padding: '48px 0',
  },
  boxContainerMobile: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  avonLogo: {
    marginBottom: 42,
  },
  marketBox: {
    marginLeft: p => (p.deviceType === 'mobile' ? 18 : 71),
    marginRight: p => (p.deviceType === 'mobile' ? 18 : 71),
    width: p => (p.deviceType === 'mobile' ? '90%' : 'unset'),
  },
  dropdownBox: {
    marginTop: 18,
    marginBottom: 42,
  },
  selectText: {
    margin: p => (p.deviceType === 'mobile' ? 'unset' : '0 16px'),
    textAlign: 'center',
    paddingBottom: '30px',
  },
  textStyle: {
    color: '#181818',
    fontFamily: 'Montserrat-medium',
    fontWeight: 500,
    fontSize: 24,
  },
  buttonBox: {
    marginTop: 0,
  },
}));

const MarketSelect = ({
  callApi,
}) => {
  const deviceType = getDeviceType();

  const classes = useStyles({ deviceType });
  const { setError, cleanToastBanner } = useError();

  const [markets, setMarkets] = useState([]);
  const [market, setMarket] = useState();
  const [language, setLanguage] = useState();

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const res = await callApi(getMarkets());
        const allMarkets = res.jsonObject.markets || [];
        await setMarkets(allMarkets);
        cleanToastBanner();
      } catch (e) {
        setError(e.error);
      }
    };
    fetchMarkets();
  }, []);

  const getBoxContainerClasses = () => {
    switch (deviceType) {
      case 'desktop':
        return classes.boxContainer;
      case 'tablet':
        return classes.boxContainerTablet;
      case 'mobile':
        return classes.boxContainerMobile;
      default:
        return classes.boxContainer;
    }
  };

  const formBox = (
    <Box className={getBoxContainerClasses()}>
      <Grid className={classes.avonLogo}>
        <img alt="" src={avonGrowSvg} />
      </Grid>
      <Grid className={classes.marketBox}>
        <Box className={classes.selectText}>
          <Typography variant="h4" className={classes.textStyle}>Select Market & Language</Typography>
        </Box>
        <Box>
          <Dropdown
            fullWidth
            placeholder="SELECT YOUR MARKET"
            selectOptions={
              markets.map(m => ({ label: `${m.mrktNm} (${m.mrktDsplyNm})`, value: m.mrktCd }))}
            defaultValue={market}
            onChange={async (value) => {
              await setMarket(value);
              const defaultLang = markets.filter(m => m.mrktCd === value)[0].languages[0].langCd;
              const defaultLangDisplay = markets.filter(m => m.mrktCd === value)[0]
                .languages[0].langDsplyNm;
              await setLanguage(defaultLang || '');
              // sessionStorage.setItem('mrktCd', value);
              cookies.save('mrktCd', value, { path: '/' });
              // sessionStorage.setItem('langCd', defaultLang);
              cookies.save('langCd', defaultLang, { path: '/' });
              cookies.save('langDisplay', defaultLangDisplay, { path: '/' });
              cookies.save('mrktName', markets.filter(m => m.mrktCd === value)[0].mrktDsplyNm, { path: '/' });
              cookies.save('mrktFlag', markets.filter(m => m.mrktCd === value)[0].mrktFlgImgUrl, { path: '/' });
            }}
            MenuWidth="332px"
            MenuHeight="271px"
            selectStyle={{ height: '47px', alignItems: 'flex-end' }}
            menuItemStyle={{ color: '#181818' }}
          />
        </Box>
        <Box className={classes.dropdownBox}>
          <Dropdown
            fullWidth
            placeholder="SELECT LANGUAGE"
            selectOptions={
              market
                ? markets.filter(m => m.mrktCd === market)[0].languages
                  .map(l => ({ label: `${l.langNm} (${l.langDsplyNm})`, value: l.langCd, direction: l.langCd === 'ar' ? 'rtl' : 'ltr' }))
                : []
            }
            defaultValue={language}
            onChange={(value) => {
              setLanguage(value);
              // sessionStorage.setItem('langCd', value);
              cookies.save('langCd', value, { path: '/' });
              cookies.save('langDisplay', markets.filter(m => m.mrktCd === market)[0].languages.filter(l => l.langCd === value)[0].langDsplyNm, { path: '/' });
            }}
            selectStyle={{ height: '47px', alignItems: 'flex-end' }}
            MenuWidth="332px"
            MenuHeight="271px"
            menuItemStyle={{ color: '#181818' }}
          />
        </Box>
        <Box mt={5} className={classes.buttonBox}>
          <Button
            priority="primary"
            name="market-select-confirm"
            id="market-select-confirm"
            onClick={() => { window.location.reload(); }}
            disabled={!market || !language}
          >
            SAVE
          </Button>
        </Box>
      </Grid>
    </Box>
  );

  return (
    <>
      {deviceType === 'mobile' ? (formBox) : (
        <div className={classes.pageContainer}>
          <div className={classes.cardContainer}>
            {formBox}
          </div>
        </div>
      )}
    </>
  );
};

MarketSelect.propTypes = {
  callApi: func.isRequired,
};

export default MarketSelect;
